import React, { useState, useEffect, useCallback, useRef } from 'react';
import { glassSurface } from './config/glassTheme';
import { useNavigate } from 'react-router-dom';
import API from './api';
import ProductNav from './components/ProductNav';
import SideHelpPanel from './components/SideHelpPanel';

const PAGE_SIZE = 30;

const LAYER_KEY_SETS = {
  's1-title':  ['primarySubject','species','roleArchetype','factionAlignment','originBackground','rarityStatus','ageTier','genderExpression','conceptualContext','styleIntensity','entityCount','narrativeTone'],
  'sx-title':  ['constructionMaterials','surfaceFinish','technologyBaseline','condition','arrangement','l2Pose','l2Stance','l2BodyType','l2Scale','l2Condition','l2Movement','l2LimbPos','l2Orientation','l2Formation','l2Grip','l2UpperBody','l2Spacing','l2Mood','l2Facial','l2Outfit','l2Material','l2OutfitStyle','l2ClothingColour','l2WeaponType','l2WeaponDetail','l2WeaponColour','l2WeaponUsage','l2CompanionType','l2CompanionDetail','l2CompanionQty','l2Interaction','primaryModifiers','secondaryModifiers','artObjects','special','structuralElements','fastenerDetails','eraExclusions'],
  's2-title':  ['presenceLevel','activityState'],
  's25-title': ['advFlavour'],
  's3a-title': ['avoid'],
  's3-title':  ['renderStyle','camera','framing','detailIntensity','scale','lighting','mood','lensAndPerspective','subjectEmphasis','depthOfField','colourPalette','atmosphereEffects'],
};

function countLayerFields(cfg, layerKeys) {
  if (!cfg || typeof cfg !== 'object') return 0;
  return layerKeys.filter(k => {
    const v = cfg[k];
    if (!v) return false;
    if (Array.isArray(v)) return v.length > 0;
    return typeof v === 'string' && v.trim() !== '';
  }).length;
}

function detectLastLayer(cfg) {
  if (!cfg || typeof cfg !== 'object') return 's1-title';
  let bestLayer = 's1-title';
  let bestCount = 0;
  Object.entries(LAYER_KEY_SETS).forEach(([layerId, keys]) => {
    const count = countLayerFields(cfg, keys);
    if (count > bestCount) { bestCount = count; bestLayer = layerId; }
  });
  return bestLayer;
}

function detectIntent(build) {
  const cfg = build.configuration_parameters || {};
  const variantCount = build.variant_count || 0;
  const totalFields = Object.values(LAYER_KEY_SETS).flat().filter(k => {
    const v = cfg[k];
    if (!v) return false;
    if (Array.isArray(v)) return v.length > 0;
    return typeof v === 'string' && v.trim() !== '';
  }).length;
  if (variantCount >= 2) return 'variation';
  const layersCovered = Object.entries(LAYER_KEY_SETS).filter(([, keys]) =>
    countLayerFields(cfg, keys) > 0
  ).length;
  if (layersCovered >= 4 || totalFields >= 10) return 'exploration';
  return 'refinement';
}

function writeRehydrate(build, isVariation, navigate) {
  if (!build.generator_slug) return;
  try {
    const slug = build.generator_slug;
    let configuration = {};
    try {
      const saved = localStorage.getItem('visinvoke_state_' + slug);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.state && typeof parsed.state === 'object') {
          configuration = parsed.state;
        }
      }
    } catch (e) {
      console.warn('VISINVOKE: localStorage restore failed, falling back to job config', e);
    }
    if (!configuration || Object.keys(configuration).length === 0) {
      configuration = build.configuration_parameters || {};
    }
    const payload = {
      slug: slug,
      configuration: configuration,
    };
    sessionStorage.setItem('vi_build', JSON.stringify(payload));
  } catch (e) {}
  navigate('/engine', { state: { slug: build.generator_slug } });
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatRelativeTime(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return formatDate(iso);
}

function typeLabel(type) {
  if (type === 'template') return 'Template';
  if (type === 'preset')   return 'Preset';
  if (type === 'history')  return 'History';
  return null;
}

function jobToBuild(job) {
  return {
    build_id:                 job.job_id,
    generator_name:           job.generator_slug
      ? job.generator_slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      : 'Generator',
    generator_slug:           job.generator_slug || '',
    build_name:               job.generator_slug
      ? job.generator_slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      : 'Generation',
    created_date:             job.completed_at || job.created_at,
    type:                     'recent',
    status:                   job.status,
    preview_image:            job.result ? job.result.url : null,
    configuration_parameters: job.configuration_parameters || {},
    seed:                     job.seed || null,
    collections:              ['all'],
    variant_count:            job.variant_count || 0,
  };
}

function sortBySuccess(builds) {
  return [...builds].sort((a, b) => {
    const scoreA = a.status === 'completed' && a.preview_image ? 2 : a.status === 'completed' ? 1 : 0;
    const scoreB = b.status === 'completed' && b.preview_image ? 2 : b.status === 'completed' ? 1 : 0;
    if (scoreB !== scoreA) return scoreB - scoreA;
    return new Date(b.created_date) - new Date(a.created_date);
  });
}

function BuildCard({ build, onAction, isRecent }) {
  const navigate = useNavigate();
  const [hovered,  setHovered]  = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pressing, setPressing] = useState(false);
  const menuRef = useRef(null);

  const label      = typeLabel(build.type);
  const dateStr    = formatRelativeTime(build.created_date);
  const isFailed   = build.status === 'failed';
  const canAnimate   = build.status === 'completed' && !!build.preview_image;
  const canQuickEdit = build.status === 'completed' && !!build.preview_image;
  const intent     = detectIntent(build);
  const variationIsStrong = intent === 'variation';

  const continueBtnLabel = intent === 'variation' ? 'New Variation' : 'Continue Build';

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const handleContinue = (e) => {
    e.stopPropagation();
    setPressing(true);
    setTimeout(() => setPressing(false), 200);
    onAction('open', build);
  };

  const handleVariation = (e) => {
    e.stopPropagation();
    setPressing(true);
    setTimeout(() => setPressing(false), 200);
    onAction('variation', build);
  };

  const handleQuickEdit = (e) => {
    e.stopPropagation();
    navigate(`/quick-edit?build_id=${build.build_id}`);
  };

  return (
    <div
      className={isRecent ? 'vi-card-recent' : undefined}
      style={{
        ...bc.card,
        ...(hovered && !isFailed ? bc.cardHover : {}),
        opacity: isFailed ? 0.82 : 1,
        filter: isFailed ? 'brightness(0.82) saturate(0.7)' : 'none',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ ...bc.imgArea, ...(build.preview_image ? {} : bc.imgAreaEmpty) }}>
        {build.preview_image
          ? <img src={build.preview_image} alt={build.build_name} style={bc.img} />
          : <div style={bc.imgPlaceholder}><span style={bc.imgIcon}>&#x1F5BC;</span></div>
        }
        {label && (
          <div style={{ ...bc.typeBadge, background: label === 'Template' ? 'rgba(16,185,129,0.9)' : label === 'Preset' ? 'rgba(245,158,11,0.9)' : 'rgba(100,116,139,0.85)' }}>
            {label}
          </div>
        )}
        {build.status && build.status !== 'completed' && (
          <div style={{ ...bc.typeBadge, left: 'auto', right: 8, background: isFailed ? 'rgba(220,38,38,0.9)' : 'rgba(245,158,11,0.9)', fontWeight: 800 }}>
            {build.status}
          </div>
        )}
      </div>

      <div style={bc.body}>
        <div style={bc.buildName}>{build.build_name}</div>
        <div style={bc.metaRow}>
          <span style={bc.metaLabel}>Generator</span>
          <span style={bc.metaVal}>{build.generator_name}</span>
        </div>
        <div style={bc.metaRow}>
          <span style={bc.metaLabel}>Created</span>
          <span style={bc.metaVal}>{dateStr}</span>
        </div>
      </div>

      <style>{CARD_BTN_CSS}</style>

      <div style={bc.actions}>
        <button
          className="vi-card-btn"
          title="Continue building with this configuration"
          style={{
            ...bc.btnContinue,
            ...(variationIsStrong ? bc.btnContinueSecondary : {}),
            transform: pressing ? 'scale(0.97)' : 'scale(1)',
            filter: pressing ? 'brightness(0.92)' : 'brightness(1)',
          }}
          onClick={handleContinue}
        >
          {continueBtnLabel}
        </button>

        <button
          className="vi-card-btn"
          title="Generate a new variation using this configuration"
          style={{
            ...bc.btnVariation,
            ...(variationIsStrong ? bc.btnVariationStrong : {}),
          }}
          onClick={handleVariation}
        >
          Variation
        </button>

        {canQuickEdit && (
          <button
            className="vi-card-btn"
            title="Adjust camera, lighting and style with sliders"
            style={bc.btnQuickEdit}
            onClick={handleQuickEdit}
          >
            Quick Edit
          </button>
        )}

        <button
          className="vi-card-btn"
          title="Save configuration as a preset"
          style={bc.btnPreset}
          onClick={e => { e.stopPropagation(); onAction('savePreset', build); }}
        >
          Save Preset
        </button>

        <div style={bc.inlineActions}>
          {canAnimate && (
            <button className="vi-card-btn" style={bc.btnInlineAnimate} onClick={e => { e.stopPropagation(); navigate(`/animate?image_id=${build.build_id}`); }}>&#9654; Animate</button>
          )}
          <button className="vi-card-btn" style={bc.btnInlineDupe} onClick={e => { e.stopPropagation(); onAction('duplicate', build); }}>Duplicate</button>
          <button className="vi-card-btn" style={bc.btnInlineCol} onClick={e => { e.stopPropagation(); onAction('addToCol', build); }}>+Collection</button>
          <button className="vi-card-btn" style={bc.btnInlineDel} onClick={e => { e.stopPropagation(); onAction('delete', build); }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

const CARD_BTN_CSS = `
  .vi-card-btn { transition: filter 0.12s, transform 0.1s, box-shadow 0.12s; }
  .vi-card-btn:hover { filter: brightness(1.15); transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.28); }
  .vi-card-btn:active { filter: brightness(0.88) !important; transform: scale(0.97) !important; box-shadow: none !important; }
  .vi-card-recent { border-color: rgba(139,92,246,0.35) !important; box-shadow: 0 4px 20px rgba(139,92,246,0.15) !important; }
  .vi-search-input::placeholder { color: rgba(255,255,255,0.55); }
`;

const bc = {
  card:        { position: 'relative', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14, overflow: 'visible', display: 'flex', flexDirection: 'column', transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s', cursor: 'pointer' },
  cardHover:   { borderColor: 'rgba(139,92,246,0.55)', boxShadow: '0 8px 32px rgba(139,92,246,0.22)', transform: 'translateY(-2px)' },
  imgArea:     { height: 220, position: 'relative', flexShrink: 0, borderRadius: '14px 14px 0 0', overflow: 'hidden' },
  imgAreaEmpty: { height: 72 },
  img:         { width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' },
  imgPlaceholder: { width: '100%', height: '100%', background: 'rgba(139,92,246,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  imgIcon:     { fontSize: 22, opacity: 0.18 },
  typeBadge:   { position: 'absolute', top: 8, left: 8, color: '#fff', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', padding: '3px 8px', borderRadius: 6, backdropFilter: 'blur(4px)' },
  body:        { padding: '10px 12px 6px', display: 'flex', flexDirection: 'column', gap: 4, flex: 1 },
  buildName:   { fontWeight: 600, fontSize: 13, color: '#FFFFFF', lineHeight: 1.35 },
  metaRow:     { display: 'flex', gap: 8, alignItems: 'baseline' },
  metaLabel:   { fontSize: 10, color: 'rgba(255,255,255,0.75)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', minWidth: 54 },
  metaVal:     { fontSize: 11, color: 'rgba(255,255,255,0.75)' },
  actions:     { display: 'flex', gap: 4, padding: '8px 10px 10px', flexWrap: 'wrap', alignItems: 'center' },
  btnContinue: { ...glassSurface('#5B21B6', '#8b5cf6', '#a855f7'), borderRadius: 7, padding: '7px 12px', fontSize: 11, fontWeight: 800, cursor: 'pointer', flex: '1 1 auto', letterSpacing: '0.01em', transition: 'filter 0.12s, transform 0.1s, box-shadow 0.12s', opacity: 1 },
  btnContinueSecondary: { ...glassSurface('#5B21B6', '#8b5cf6', '#a855f7'), fontWeight: 600 },
  btnVariation: { ...glassSurface('#5B21B6', '#8b5cf6', '#a855f7'), borderRadius: 7, padding: '6px 9px', fontSize: 11, fontWeight: 500, cursor: 'pointer', opacity: 0.75 },
  btnVariationStrong: { ...glassSurface('#5B21B6', '#8b5cf6', '#a855f7'), fontWeight: 800, flex: '1 1 auto' },
  btnQuickEdit: { ...glassSurface('#A66D05', '#f59e0b', '#FBBF24'), borderRadius: 7, padding: '6px 9px', fontSize: 11, fontWeight: 700, cursor: 'pointer', opacity: 0.92 },
  btnPreset:   { ...glassSurface('#A66D05', '#f59e0b', '#FBBF24'), borderRadius: 7, padding: '6px 9px', fontSize: 11, fontWeight: 500, cursor: 'pointer', opacity: 0.7 },
  btnMore:     { ...glassSurface('#334155', '#64748B', '#94A3B8'), borderRadius: 7, padding: '6px 8px', fontSize: 12, fontWeight: 700, cursor: 'pointer', letterSpacing: 2, opacity: 0.65 },
  inlineActions: { display: 'flex', gap: 4, padding: '4px 10px 10px', flexWrap: 'nowrap', alignItems: 'center' },
  btnInlineAnimate: { flex: 1, ...glassSurface('#1E40AF', '#3b82f6', '#93C5FD'), borderRadius: 6, padding: '5px 0', fontSize: 10, fontWeight: 700, cursor: 'pointer', textAlign: 'center', whiteSpace: 'nowrap' },
  btnInlineDupe:   { flex: 1, background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, padding: '5px 0', fontSize: 10, fontWeight: 700, cursor: 'pointer', textAlign: 'center', whiteSpace: 'nowrap' },
  btnInlineCol:    { flex: 1, background: 'rgba(139,92,246,0.18)', color: '#c4b5fd', border: '1px solid rgba(139,92,246,0.3)', borderRadius: 6, padding: '5px 0', fontSize: 10, fontWeight: 700, cursor: 'pointer', textAlign: 'center', whiteSpace: 'nowrap' },
  btnInlineDel:    { flex: 1, background: 'rgba(220,38,38,0.12)', color: '#f87171', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 6, padding: '5px 0', fontSize: 10, fontWeight: 700, cursor: 'pointer', textAlign: 'center', whiteSpace: 'nowrap' },
  menu:        { position: 'absolute', bottom: '100%', right: 0, background: '#1a1740', border: '1px solid rgba(139,92,246,0.3)', borderRadius: 8, padding: '4px 0', minWidth: 145, zIndex: 50, boxShadow: '0 8px 28px rgba(0,0,0,0.55)' },
  menuItem:    { display: 'block', width: '100%', background: 'none', border: 'none', color: 'rgba(255,255,255,0.78)', padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', textAlign: 'left' },
  menuItemPromoted: { color: '#fff', fontWeight: 700, background: 'rgba(139,92,246,0.1)' },
  menuItemDanger: { color: '#f87171' },
};

function EmptyState({ tab, loading }) {
  if (loading) return <div style={es.wrap}><div style={es.sub}>Loading your generations...</div></div>;
  const msgs = {
    recent:   { icon: '🗂️', title: 'No generations yet.', sub: 'Start generating images to build your history.' },
    preset:   { icon: '⭐', title: 'No presets saved yet.', sub: 'Save builds to quickly reuse configurations.' },
    template: { icon: '📋', title: 'No templates available.', sub: 'Templates are reusable configuration starting points.' },
    history:  { icon: '📜', title: 'No build history yet.', sub: 'Your generation history will appear here automatically.' },
  };
  const m = msgs[tab] || msgs.recent;
  return (
    <div style={es.wrap}>
      <div style={es.icon}>{m.icon}</div>
      <div style={es.title}>{m.title}</div>
      <div style={es.sub}>{m.sub}</div>
    </div>
  );
}

const es = {
  wrap:  { textAlign: 'center', padding: '60px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 },
  icon:  { fontSize: 48, opacity: 0.35 },
  title: { fontSize: 18, fontWeight: 700, color: 'rgba(255,255,255,0.75)' },
  sub:   { fontSize: 14, color: 'rgba(255,255,255,0.4)', maxWidth: 360, lineHeight: 1.6 },
};

function NameModal({ title, initialValue, onConfirm, onCancel }) {
  const [val, setVal] = useState(initialValue || '');
  return (
    <div style={nm.overlay} onClick={onCancel}>
      <div style={nm.modal} onClick={e => e.stopPropagation()}>
        <div style={nm.title}>{title}</div>
        <input style={nm.input} value={val} onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') onConfirm(val); if (e.key === 'Escape') onCancel(); }}
          autoFocus placeholder="Enter name..." />
        <div style={nm.btns}>
          <button style={nm.cancel} onClick={onCancel}>Cancel</button>
          <button style={nm.confirm} onClick={() => onConfirm(val)} disabled={!val.trim()}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

const nm = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  modal:   { background: '#1e1b4b', border: '1px solid rgba(139,92,246,0.3)', borderRadius: 14, padding: 28, width: '100%', maxWidth: 380, display: 'flex', flexDirection: 'column', gap: 16 },
  title:   { fontWeight: 800, fontSize: 17, color: '#fff' },
  input:   { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 14, color: '#fff', outline: 'none' },
  btns:    { display: 'flex', gap: 10, justifyContent: 'flex-end' },
  cancel:  { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, padding: '9px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  confirm: { background: 'linear-gradient(135deg, #8b5cf6, #a855f7)', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
};

const rhp = {
  heading: { fontWeight: 700, fontSize: 13, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 0, marginBottom: 8, display: 'block' },
  list:    { margin: 0, padding: '0 0 4px 16px', display: 'flex', flexDirection: 'column', gap: 0 },
  item:    { fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.55, marginBottom: 6, borderRadius: 6, padding: '2px 4px' },
  bold:    { fontWeight: 700, color: 'rgba(255,255,255,0.88)' },
  divider: { height: 1, background: 'rgba(255,255,255,0.05)', margin: '12px 0' },
};

const SCROLLBAR_STYLE_RHP = `
  .vsi-rhp::-webkit-scrollbar { width: 4px; }
  .vsi-rhp::-webkit-scrollbar-track { background: transparent; }
  .vsi-rhp::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 4px; }
  .vsi-rhp::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.22); }
`;

function RightHelpPanel() {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <style>{SCROLLBAR_STYLE_RHP}</style>
      <button
        style={{
          position: 'fixed', right: open ? 400 : 0, top: '50%', zIndex: 1001,
          background: 'linear-gradient(180deg, #1e2a3a 0%, #0f1825 100%)',
          border: '1px solid rgba(255,255,255,0.15)', borderRight: open ? '1px solid rgba(255,255,255,0.15)' : 'none',
          borderRadius: '8px 0 0 8px', color: 'rgba(255,255,255,0.75)', cursor: 'pointer',
          padding: '12px 7px', fontSize: 13, fontWeight: 700, letterSpacing: '0.08em',
          writingMode: 'vertical-rl', textOrientation: 'mixed',
          transform: open ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
          transition: 'right 0.28s ease', boxShadow: '-2px 0 12px rgba(0,0,0,0.35)', userSelect: 'none',
        }}
        onClick={() => setOpen(o => !o)}
      >
        {open ? '✕' : 'GUIDE'}
      </button>
      <div className="vsi-rhp" style={{ position: 'fixed', top: 0, right: 0, width: 400, height: '100vh', zIndex: 1000, background: 'linear-gradient(180deg, #0B0F1A 0%, #121826 100%)', borderLeft: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 0 20px rgba(0,0,0,0.5)', transform: open ? 'translateX(0)' : 'translateX(400px)', transition: 'transform 0.28s ease', display: 'flex', flexDirection: 'column', padding: '20px 24px', boxSizing: 'border-box', overflowY: 'auto' }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: '#FFFFFF', marginBottom: 14, paddingBottom: 6, borderBottom: '2px solid rgba(168,85,247,0.6)' }}>Advanced Workspace Guide</div>
        <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 20 }}>
          <div><span style={rhp.heading}>Understanding Your Work</span><ul style={rhp.list}><li style={rhp.item}><span style={rhp.bold}>Build</span> — A generated result you can reopen and modify</li><li style={rhp.item}><span style={rhp.bold}>Preset</span> — A saved configuration for reuse</li><li style={rhp.item}><span style={rhp.bold}>Template</span> — A structured starting point</li><li style={rhp.item}><span style={rhp.bold}>History</span> — Your record of generated outputs</li></ul></div>
          <div style={rhp.divider} />
          <div style={{ marginTop: 18 }}><span style={rhp.heading}>Workflow</span><ul style={rhp.list}><li style={rhp.item}>Generate content — it appears in this workspace</li><li style={rhp.item}>Continue Build — return to generator with same settings</li><li style={rhp.item}>Use Generate Variation to re-run with same config</li><li style={rhp.item}>Quick Edit — adjust camera, lighting and style with sliders</li><li style={rhp.item}>Save presets to reuse configurations</li></ul></div>
          <div style={rhp.divider} />
          <div style={{ marginTop: 18 }}><span style={rhp.heading}>Power Tips</span><ul style={rhp.list}><li style={rhp.item}>Presets are best for consistency across multiple outputs</li><li style={rhp.item}>Collections help manage large projects</li><li style={rhp.item}>Filters are faster than scrolling large libraries</li></ul></div>
        </div>
      </div>
      {open && <div style={{ position: 'fixed', inset: 0, zIndex: 999, background: 'rgba(0,0,0,0.35)' }} onClick={() => setOpen(false)} />}
    </>
  );
}

export default function Workspace() {
  const navigate = useNavigate();

  const [activeTab,        setActiveTab]        = useState('recent');
  const [search,           setSearch]           = useState('');
  const [filterGen,        setFilterGen]        = useState('All Generators');
  const [filterType,       setFilterType]       = useState('All');
  const [filterDate,       setFilterDate]       = useState('All Time');
  const [filterSort,       setFilterSort]       = useState('Newest');
  const [collections,      setCollections]      = useState([{ id: 'all', name: 'All Builds', count: 0 }]);
  const [activeCollection, setActiveCollection] = useState('all');
  const [collModal,        setCollModal]        = useState(null);
  const [activeGenerator,  setActiveGenerator]  = useState('All Builds');
  const [genPanelOpen,     setGenPanelOpen]     = useState(true);
  const [genSearch,        setGenSearch]        = useState('');
  const [builds,           setBuilds]           = useState([]);
  const [generators,       setGenerators]       = useState([{ name: 'All Builds', slug: 'all-builds' }]);
  const [loading,          setLoading]          = useState(true);
  const [displayed,        setDisplayed]        = useState(PAGE_SIZE);
  const [nameModal,        setNameModal]        = useState(null);
  const loaderRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    API.get('/v1/images/jobs?limit=100')
      .then(res => {
        let jobs = [];
        if (res.data) {
          if (Array.isArray(res.data)) jobs = res.data;
          else if (Array.isArray(res.data.jobs)) jobs = res.data.jobs;
          else if (Array.isArray(res.data.results)) jobs = res.data.results;
        }
        const mapped = jobs.map(jobToBuild);
        setBuilds(mapped);
        const slugSet = new Set();
        const genList = [{ name: 'All Builds', slug: 'all-builds' }];
        jobs.forEach(j => {
          if (j.generator_slug && !slugSet.has(j.generator_slug)) {
            slugSet.add(j.generator_slug);
            genList.push({ slug: j.generator_slug, name: j.generator_slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') });
          }
        });
        setGenerators(genList);
        setCollections([{ id: 'all', name: 'All Builds', count: mapped.length }]);
      })
      .catch(() => { setBuilds([]); setGenerators([{ name: 'All Builds', slug: 'all-builds' }]); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!loaderRef.current) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) setDisplayed(prev => Math.min(prev + PAGE_SIZE, sorted.length));
    }, { threshold: 0.1 });
    obs.observe(loaderRef.current);
    return () => obs.disconnect();
  });

  const filtered = builds.filter(b => {
    if (activeTab !== 'recent' && b.type !== activeTab) return false;
    if (activeCollection !== 'all' && !(b.collections || []).includes(activeCollection)) return false;
    if (activeGenerator !== 'All Builds' && b.generator_name !== activeGenerator) return false;
    if (filterType !== 'All' && b.type !== filterType.toLowerCase()) return false;
    if (filterDate !== 'All Time') {
      const created = new Date(b.created_date);
      const now = new Date();
      if (filterDate === 'Today') { if (created < new Date(now.getFullYear(), now.getMonth(), now.getDate())) return false; }
      else if (filterDate === 'Last 7 Days') { if (created < new Date(now - 7 * 86400000)) return false; }
      else if (filterDate === 'Last 30 Days') { if (created < new Date(now - 30 * 86400000)) return false; }
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      return b.build_name.toLowerCase().includes(q) || b.generator_name.toLowerCase().includes(q);
    }
    return true;
  });

  const sorted = (() => {
    if (filterSort === 'Newest') return sortBySuccess(filtered);
    if (filterSort === 'Oldest') return [...filtered].sort((a, b) => new Date(a.created_date) - new Date(b.created_date));
    if (filterSort === 'Alphabetical') return [...filtered].sort((a, b) => a.build_name.localeCompare(b.build_name));
    if (filterSort === 'Generator') return [...filtered].sort((a, b) => a.generator_name.localeCompare(b.generator_name));
    return filtered;
  })();

  const visible = sorted.slice(0, displayed);

  const handleAction = useCallback((action, build) => {
    if (action === 'open')      { writeRehydrate(build, false, navigate); }
    else if (action === 'variation') { writeRehydrate(build, true, navigate); }
    else if (action === 'delete') { setBuilds(prev => prev.filter(b => b.build_id !== build.build_id)); }
    else if (action === 'duplicate') {
      setBuilds(prev => [{ ...build, build_id: `dup_${Date.now()}`, build_name: `${build.build_name} (Copy)`, created_date: new Date().toISOString(), type: 'recent', variant_count: 0 }, ...prev]);
    } else if (action === 'savePreset') {
      setNameModal({
        title: 'Save as Preset', initial: build.build_name,
        onConfirm: name => {
          setBuilds(prev => [{ ...build, build_id: `preset_${Date.now()}`, build_name: name, type: 'preset', created_date: new Date().toISOString() }, ...prev]);
          setNameModal(null);
        },
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateCollection = name => { if (!name.trim()) return; setCollections(prev => [...prev, { id: `col_${Date.now()}`, name: name.trim(), count: 0 }]); setCollModal(null); };
  const handleRenameCollection = (id, name) => { if (!name.trim()) return; setCollections(prev => prev.map(c => c.id === id ? { ...c, name: name.trim() } : c)); setCollModal(null); };
  const handleDeleteCollection = id => { setCollections(prev => prev.filter(c => c.id !== id)); if (activeCollection === id) setActiveCollection('all'); };

  const TABS = [
    { key: 'recent', label: 'Recent Builds' }, { key: 'preset', label: 'Saved Presets' },
    { key: 'template', label: 'Templates' },   { key: 'history', label: 'History' },
  ];

  return (
    <div style={ws.page}>
      <ProductNav />
      <SideHelpPanel title={getWorkspaceHelp(activeTab).title}>{getWorkspaceHelp(activeTab).body}</SideHelpPanel>
      <RightHelpPanel />

      {nameModal && <NameModal title={nameModal.title} initialValue={nameModal.initial} onConfirm={nameModal.onConfirm} onCancel={() => setNameModal(null)} />}
      {collModal && (
        <NameModal
          title={collModal.mode === 'create' ? 'New Collection' : 'Rename Collection'}
          initialValue={collModal.mode === 'rename' ? collModal.name : ''}
          onConfirm={name => { if (collModal.mode === 'create') handleCreateCollection(name); else handleRenameCollection(collModal.id, name); }}
          onCancel={() => setCollModal(null)} />
      )}

      <div style={ws.container}>
        <div style={ws.header}>
          <div>
            <h1 style={ws.title}>Build Workspace</h1>
            <p style={ws.subtitle}>Manage saved builds, presets, templates and configuration history.</p>
            <p style={ws.trustSignal}>&#x2713; Builds auto-saved &nbsp;&bull;&nbsp; Safe to return anytime</p>
          </div>
        </div>

        <div style={ws.tabs}>
          {TABS.map(t => (
            <button key={t.key} style={{ ...ws.tab, ...(activeTab === t.key ? ws.tabActive : {}) }}
              onClick={() => { setActiveTab(t.key); setDisplayed(PAGE_SIZE); }}>{t.label}</button>
          ))}
        </div>

        <div style={ws.searchRow}>
          <div style={ws.searchWrap}>
            <span style={ws.searchIcon}>&#x1F50D;</span>
            <input style={ws.searchInput} placeholder="Search builds, generators, presets..."
              value={search} onChange={e => { setSearch(e.target.value); setDisplayed(PAGE_SIZE); }} />
            {search && <button style={ws.searchClear} onClick={() => setSearch('')}>&#x2715;</button>}
          </div>
          <div style={ws.filters}>
            <select style={ws.select} value={filterGen} onChange={e => setFilterGen(e.target.value)}>
              {generators.map(g => <option key={g.slug} value={g.name}>{g.name}</option>)}
            </select>
            <select style={ws.select} value={filterType} onChange={e => setFilterType(e.target.value)}>
              {['All', 'Recent', 'Preset', 'Template', 'History'].map(t => <option key={t}>{t}</option>)}
            </select>
            <select style={ws.select} value={filterDate} onChange={e => setFilterDate(e.target.value)}>
              {['All Time', 'Today', 'Last 7 Days', 'Last 30 Days'].map(d => <option key={d}>{d}</option>)}
            </select>
            <select style={ws.select} value={filterSort} onChange={e => setFilterSort(e.target.value)}>
              {['Newest', 'Oldest', 'Alphabetical', 'Generator'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div style={ws.body}>
          <div style={ws.sidebar}>
            <div style={ws.sideSection}>
              <div style={ws.sideSectionHeader}>
                <span style={ws.sideSectionTitle}>Collections</span>
                <button style={ws.sideAddBtn} onClick={() => setCollModal({ mode: 'create' })} title="New Collection">+</button>
              </div>
              <div style={ws.sideList}>
                {collections.map(col => (
                  <div key={col.id} style={{ ...ws.sideItem, ...(activeCollection === col.id ? ws.sideItemActive : {}) }}>
                    <button style={ws.sideItemBtn} onClick={() => { setActiveCollection(col.id); setDisplayed(PAGE_SIZE); }}>
                      <span style={ws.sideItemName}>{col.name}</span>
                      <span style={ws.sideItemCount}>{col.count}</span>
                    </button>
                    {col.id !== 'all' && (
                      <div style={ws.sideItemActions}>
                        <button style={ws.sideActionBtn} onClick={() => setCollModal({ mode: 'rename', id: col.id, name: col.name })}>&#x270E;</button>
                        <button style={{ ...ws.sideActionBtn, color: '#f87171' }} onClick={() => handleDeleteCollection(col.id)}>&#x1F5D1;</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div style={ws.sideSectionGen}>
              <div style={ws.sideSectionHeader}>
                <button style={ws.sideCollapseBtn} onClick={() => setGenPanelOpen(v => !v)}>
                  <span style={ws.sideSectionTitle}>Generators</span>
                  <span style={ws.sideChevron}>{genPanelOpen ? '▾' : '▸'}</span>
                </button>
              </div>
              {genPanelOpen && (
                <>
                  <div style={ws.genSearchWrap}>
                    <span style={ws.genSearchIcon}>&#x1F50D;</span>
                    <input style={ws.genSearchInput} placeholder="Search generators..." value={genSearch} onChange={e => setGenSearch(e.target.value)} />
                    {genSearch && <button style={ws.genSearchClear} onClick={() => setGenSearch('')}>&#x2715;</button>}
                  </div>
                  <div style={ws.sideListGen}>
                    {(() => {
                      const filteredGens = generators.filter(g => g.name.toLowerCase().includes(genSearch.toLowerCase()));
                      if (filteredGens.length === 0) return <div style={ws.genNoResults}>No generators match</div>;
                      const allBuilds = filteredGens.filter(g => g.slug === 'all-builds');
                      const rest = filteredGens.filter(g => g.slug !== 'all-builds');
                      const groups = {};
                      rest.forEach(g => { const l = g.name.charAt(0).toUpperCase(); if (!groups[l]) groups[l] = []; groups[l].push(g); });
                      return (
                        <>
                          {allBuilds.map(g => (
                            <div key={g.slug} style={{ ...ws.sideItem, ...(activeGenerator === g.name ? ws.sideItemActive : {}) }}>
                              <button style={ws.sideItemBtn} onClick={() => { setActiveGenerator(g.name); setDisplayed(PAGE_SIZE); }}>
                                <span style={ws.sideItemName}>{g.name}</span>
                              </button>
                            </div>
                          ))}
                          {Object.keys(groups).sort().map(letter => (
                            <div key={letter}>
                              <div style={ws.genAlphaHeader}>{letter}</div>
                              {groups[letter].map(g => (
                                <div key={g.slug} style={{ ...ws.sideItem, ...(activeGenerator === g.name ? ws.sideItemActive : {}) }}>
                                  <button style={ws.sideItemBtn} onClick={() => { setActiveGenerator(g.name); setDisplayed(PAGE_SIZE); }}>
                                    <span style={ws.sideItemName}>{g.name}</span>
                                  </button>
                                </div>
                              ))}
                            </div>
                          ))}
                        </>
                      );
                    })()}
                  </div>
                </>
              )}
            </div>
          </div>

          <div style={ws.gridArea}>
            <div style={ws.resultMeta}>
              <span style={ws.resultCount}>{sorted.length} build{sorted.length !== 1 ? 's' : ''}</span>
              {(search || filterGen !== 'All Generators' || filterType !== 'All' || filterDate !== 'All Time') && (
                <button style={ws.clearFiltersBtn} onClick={() => { setSearch(''); setFilterGen('All Generators'); setFilterType('All'); setFilterDate('All Time'); }}>Clear filters</button>
              )}
            </div>

            {loading || visible.length === 0 ? (
              <EmptyState tab={activeTab} loading={loading} />
            ) : (
              <>
                <div style={ws.grid}>
                  {visible.map((build, idx) => <BuildCard key={build.build_id} build={build} onAction={handleAction} isRecent={idx === 0} />)}
                </div>
                {displayed < sorted.length && (
                  <div ref={loaderRef} style={ws.loaderSentinel}>
                    <div style={ws.loaderDot} /><div style={ws.loaderDot} /><div style={ws.loaderDot} />
                  </div>
                )}
                {displayed >= sorted.length && sorted.length > 0 && <div style={ws.endMsg}>All {sorted.length} builds loaded</div>}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const ws = {
  page:      { minHeight: '100vh', background: 'linear-gradient(135deg, #1e1b4b 0%, #334155 50%, #881337 100%)', fontFamily: "'Segoe UI', sans-serif" },
  container: { maxWidth: 1400, margin: '0 auto', padding: '28px 32px' },
  header:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 16 },
  title:     { color: '#fff', fontSize: 28, fontWeight: 800, margin: '0 0 4px' },
  subtitle:  { color: 'rgba(255,255,255,0.75)', fontSize: 15, margin: '0 0 6px', fontWeight: 500 },
  trustSignal: { color: 'rgba(255,255,255,0.28)', fontSize: 12, margin: 0, fontStyle: 'italic' },
  backBtn:   { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, padding: '9px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },
  tabs:      { display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap' },
  tab:       { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' },
  tabActive: { background: 'linear-gradient(135deg, #8b5cf6, #a855f7)', color: '#fff', borderColor: 'transparent', boxShadow: '0 4px 12px rgba(139,92,246,0.35)' },
  searchRow:   { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' },
  searchWrap:  { position: 'relative', flex: '1 1 280px', minWidth: 200 },
  searchIcon:  { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 14, opacity: 0.5 },
  searchInput: { width: '100%', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, padding: '9px 12px 9px 34px', fontSize: 14, color: 'rgba(255,255,255,0.85)', outline: 'none', boxSizing: 'border-box' },
  searchClear: { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: 13 },
  filters:     { display: 'flex', gap: 8, flexWrap: 'wrap' },
  select:      { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: 'rgba(255,255,255,0.85)', cursor: 'pointer', outline: 'none' },
  body:        { display: 'grid', gridTemplateColumns: '220px 1fr', gap: 24, alignItems: 'start' },
  sidebar:     { display: 'flex', flexDirection: 'column', gap: 12, position: 'sticky', top: 20 },
  sideSection:    { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, overflow: 'hidden' },
  sideSectionGen: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, overflow: 'hidden' },
  sideSectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)' },
  sideSectionTitle: { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#FFFFFF' },
  sideAddBtn: { background: 'rgba(139,92,246,0.2)', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.3)', borderRadius: 6, width: 24, height: 24, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
  sideCollapseBtn: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0, gap: 6 },
  sideChevron: { fontSize: 11, color: 'rgba(255,255,255,0.35)', lineHeight: 1 },
  genSearchWrap: { position: 'relative', padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  genSearchIcon: { position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', fontSize: 11, opacity: 0.4 },
  genSearchInput: { width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '6px 26px', fontSize: 12, color: '#fff', outline: 'none', boxSizing: 'border-box' },
  genSearchClear: { position: 'absolute', right: 18, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: 11, padding: 0 },
  genNoResults: { padding: '14px', fontSize: 12, color: 'rgba(255,255,255,0.25)', textAlign: 'center' },
  sideList:    { display: 'flex', flexDirection: 'column' },
  sideListGen: { display: 'flex', flexDirection: 'column' },
  genAlphaHeader: { fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(139,92,246,0.6)', padding: '8px 14px 4px', borderTop: '1px solid rgba(255,255,255,0.05)' },
  sideItem: { display: 'flex', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.04)' },
  sideItemActive: { background: 'rgba(139,92,246,0.12)', borderLeft: '2px solid #8b5cf6' },
  sideItemBtn: { flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'none', border: 'none', padding: '9px 12px', cursor: 'pointer', textAlign: 'left' },
  sideItemName: { fontSize: 13, color: 'rgba(255,255,255,0.85)', fontWeight: 500 },
  sideItemCount: { fontSize: 11, color: 'rgba(255,255,255,0.3)', fontWeight: 600, background: 'rgba(255,255,255,0.08)', borderRadius: 10, padding: '1px 7px' },
  sideItemActions: { display: 'flex', gap: 2, paddingRight: 8 },
  sideActionBtn: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: 12, padding: '3px 4px', borderRadius: 4 },
  gridArea: { minWidth: 0 },
  resultMeta: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 },
  resultCount: { fontSize: 13, color: 'rgba(255,255,255,0.4)', fontWeight: 600 },
  clearFiltersBtn: { background: 'rgba(139,92,246,0.15)', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.25)', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 18 },
  loaderSentinel: { display: 'flex', justifyContent: 'center', gap: 8, padding: 32 },
  loaderDot: { width: 8, height: 8, borderRadius: '50%', background: 'rgba(139,92,246,0.4)' },
  endMsg: { textAlign: 'center', padding: '20px 0', fontSize: 13, color: 'rgba(255,255,255,0.25)' },
};

function getWorkspaceHelp(activeTab) {
  const wrap = (body) => <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 20, overflowY: 'auto' }}>{body}</div>;
  const hd = { fontWeight: 700, fontSize: 13, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 0, marginBottom: 8, display: 'block' };
  const li_s = { fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.55, marginBottom: 6 };
  const ul_s = { margin: 0, padding: '0 0 4px 16px', display: 'flex', flexDirection: 'column', gap: 0 };
  const div_s = { height: 1, background: 'rgba(255,255,255,0.05)', margin: '12px 0' };
  const tip_s = { fontSize: 12, color: 'rgba(255,255,255,0.5)', fontStyle: 'italic', marginTop: 4 };

  if (activeTab === 'recent') return { title: 'Working With Builds', body: wrap(<><div><span style={hd}>Understanding Builds</span><ul style={ul_s}><li style={li_s}>A build is a generated result you can reopen and modify</li><li style={li_s}>This is your active working area for iteration</li></ul></div><div style={div_s}/><div style={{ marginTop: 18 }}><span style={hd}>Workflow</span><ul style={ul_s}><li style={li_s}>Continue Build — return to generator with same settings</li><li style={li_s}>Variation — re-run same config for a different result</li><li style={li_s}>Quick Edit — adjust camera, lighting and style with sliders</li><li style={li_s}>Each new generation becomes a new build</li></ul></div><div style={div_s}/><div style={{ marginTop: 18 }}><span style={hd}>Tip</span><p style={tip_s}>Think of builds as your working versions, not final storage.</p></div></>) };
  if (activeTab === 'preset') return { title: 'Using Presets', body: wrap(<><div><span style={hd}>What Presets Do</span><ul style={ul_s}><li style={li_s}>Presets store configuration settings for reuse</li><li style={li_s}>They allow consistent output across multiple generations</li></ul></div><div style={div_s}/><div style={{ marginTop: 18 }}><span style={hd}>Tip</span><p style={tip_s}>Presets save time when generating similar content repeatedly.</p></div></>) };
  if (activeTab === 'template') return { title: 'Using Templates', body: wrap(<><div><span style={hd}>What Templates Are</span><ul style={ul_s}><li style={li_s}>Templates are structured starting configurations</li><li style={li_s}>They provide a base setup for faster creation</li></ul></div><div style={div_s}/><div style={{ marginTop: 18 }}><span style={hd}>Tip</span><p style={tip_s}>Templates reduce setup time and improve consistency.</p></div></>) };
  return { title: 'Understanding History', body: wrap(<><div><span style={hd}>What History Shows</span><ul style={ul_s}><li style={li_s}>Your complete record of generated outputs</li><li style={li_s}>All previous builds over time</li></ul></div><div style={div_s}/><div style={{ marginTop: 18 }}><span style={hd}>Tip</span><p style={tip_s}>History is your archive — builds are your active workspace.</p></div></>) };
}
